import React, { useState } from 'react';
import {
  Chip, InputLabel, TextField,
} from '@material-ui/core';
import {
  checkEmail, parseEmails,
} from 'wizards/invite/util';
import { InvalidEmailError } from 'wizards/invite/types';
import { Icon } from '@whoop/web-components';
import { UseStatePropType } from 'types/useStatePropType';
import emailsInputStyles from './emailsInputStyles';

type EmailsInputProps = {
  label: string;
  emails: string[];
  setEmails?: UseStatePropType<string[]>;
  invalidEmails?: string[];
  setInvalidEmails?: UseStatePropType<string[]>;
  contextualError?: string;
  disabled?: boolean;
  serverEmailErrors?: InvalidEmailError[];
};

function EmailsInput({
  label,
  emails,
  setEmails = () => {},
  invalidEmails = [],
  setInvalidEmails = () => {},
  contextualError = '',
  disabled = false,
  serverEmailErrors = [],
}: EmailsInputProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const classes = emailsInputStyles();

  const handleDeleteEmail = (item: string) => {
    setEmails(emails.filter((i) => i !== item));
  };

  const handleDeleteInvalidEmail = (item: string) => {
    setInvalidEmails(invalidEmails.filter((i) => i !== item));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab', ',', ' '].includes(e.key)) {
      e.preventDefault();

      const possibleEmail = value.trim();

      if (possibleEmail) {
        const emailError = checkEmail(emails, possibleEmail);
        if (emailError) {
          setError(emailError);
        } else {
          setEmails([...emails, possibleEmail]);
          setValue('');
        }
      }
    }

    if (['Backspace', 'Delete'].includes(e.key) && value === '') {
      if (invalidEmails.length > 0) {
        setInvalidEmails(invalidEmails.slice(0, -1));
      } else {
        setEmails(emails.slice(0, -1));
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');
    const { validParsedEmails, invalidParsedEmails } = parseEmails(pastedText);
    const validDistinctEmails = validParsedEmails.filter(
      (email: string) => !emails.includes(email),
    );
    const invalidDistinctEmails = invalidParsedEmails.filter(
      (email: string) => !invalidEmails.includes(email),
    );
    setEmails([...emails, ...validDistinctEmails]);
    setInvalidEmails([...invalidEmails, ...invalidDistinctEmails]);
  };

  const displayChips = (chipEmails: string[]) => (
    <>
      {chipEmails.map((email: string) => {
        const isInvalid = invalidEmails.includes(email);
        return (
          <Chip
            key={email}
            label={email}
            className={`${classes.chip} ${isInvalid && classes.errorChip}`}
            onDelete={isInvalid
              ? () => handleDeleteInvalidEmail(email)
              : () => handleDeleteEmail(email)}
            data-testid={isInvalid ? `invalid-email-${email}` : `valid-email-${email}`}
            deleteIcon={<Icon className={classes.crossIcon} name="cross" />}
            classes={{
              label: classes.chipLabel,
            }}
          />
        );
      })}
    </>
  );

  const errorText = contextualError || error;
  const activeServerEmailErrors = serverEmailErrors.filter(
    ({ email }) => invalidEmails.includes(email),
  );

  return (
    <>
      <InputLabel htmlFor="emailsInput" className={classes.emailsInputLabel}>{label}</InputLabel>
      <TextField
        id="emailsInput"
        value={value}
        error={!!(error || contextualError || activeServerEmailErrors.length)}
        classes={{ root: classes.inputBox }}
        multiline
        disabled={disabled}
        InputProps={{
          startAdornment: displayChips(emails.concat(invalidEmails)),
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
          onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event),
          onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(event),
          disableUnderline: true,
          placeholder: emails.length + invalidEmails.length > 0 ? '' : 'Separate email addresses with commas',
          classes: { root: classes.input, input: classes.textArea },
        }}
      />
      {activeServerEmailErrors.map(({ email, reason }) => (
        <div key={email} className={classes.errorText}>
          {reason === 'likely_typo'
            ? `${email} looks like a typo.`
            : `${email} is not a valid email address.`}
        </div>
      ))}
      {errorText && <div className={classes.errorText}>{errorText}</div>}
    </>
  );
}

export default EmailsInput;
